import { Banner, BannerStatus, Button } from '@/components/DesignSystem';
import { ProgramExpirationLocalStorageData } from '@/pages/CustomerDashboard/interfaces';
import {
  ProgramExpirationLearnMoreDialog,
} from '@/pages/CustomerDashboard/ProgramExpirationLearnMoreDialog';
import { LocalStorageUtility } from '@/utilities/LocalStorageUtility';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { ProgramStatus } from '@shared/enums/ProgramStatus';
import { daysFromDate, getJsDateFromString, getProgramStatus } from '@shared/utilities';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from "react-material-symbols";

interface ProgramExpirationBannerProps {
  accessToken?: string,
  program: ApiRewardsProgram,
}

const ProgramExpirationLocalStorageKey = 'program-expiration-banner-dismissed';

interface ExpirationBanner {
  daysThreshold: number,
  isDismissible: boolean,
  status: BannerStatus,
}

const ExpirationBanners: ExpirationBanner[] = [
  {
    daysThreshold: 7,
    isDismissible: false,
    status: 'error',
  },
  {
    daysThreshold: 30,
    isDismissible: false,
    status: 'warning',
  },
  {
    daysThreshold: 60,
    isDismissible: true,
    status: 'info',
  },
  {
    daysThreshold: 90,
    isDismissible: true,
    status: 'info',
  },
];

export const ProgramExpirationBanner = ({
  accessToken,
  program,
}: ProgramExpirationBannerProps) => {
  const { t } = useTranslation();
  const [shouldShow, setShouldShow] = useState(false);
  const [showLearnMoreDialog, setShowLearnMoreDialog] = useState(false);
  const [banner, setBanner] = useState<ExpirationBanner>();
  const programEndDate = getJsDateFromString(program.endDate, { endOfDay: true });
  const daysUntilProgramEnd = Math.floor(-daysFromDate(programEndDate));

  useEffect(() => {
    const programStatus = getProgramStatus(program);

    if (programStatus !== ProgramStatus.Active || daysUntilProgramEnd > 90) {
      return;
    }

    const bannerToDisplay = ExpirationBanners.find(
      (banner) => banner.daysThreshold >= daysUntilProgramEnd,
    );

    if (!bannerToDisplay) {
      return;
    }

    setBanner(bannerToDisplay);

    const dismissedData = LocalStorageUtility.getItem(ProgramExpirationLocalStorageKey);
    if (bannerToDisplay.isDismissible && dismissedData) {
      const { programId, daysThreshold } = JSON.parse(dismissedData);
      if (programId === program.id && bannerToDisplay.daysThreshold >= parseInt(daysThreshold)) {
        return;
      }
    }

    setShouldShow(true);
  }, [daysUntilProgramEnd, program]);

  const handleDismiss = () => {
    if (!banner) {
      return;
    }
    const dismissedData: ProgramExpirationLocalStorageData = {
      programId: program.id,
      daysThreshold: banner.daysThreshold,
    };
    LocalStorageUtility.set(ProgramExpirationLocalStorageKey, dismissedData);
    setShouldShow(false);
  };

  if (
    !shouldShow
    || !banner
  ) {
    return null;
  }

  const getTitle = () => {
    if (daysUntilProgramEnd > 0) {
      return t(
        $ => $.customerDashboard.programExpiration.dialog.endsInDays,
        { count: daysUntilProgramEnd },
      );
    }
    return t($ => $.customerDashboard.programExpiration.dialog.endsToday);
  };

  return (
    <>
      <Banner
        actionButtons={accessToken ? [
          <Button
            key="learn-more-button"
            onClick={() => setShowLearnMoreDialog(true)}
            testID="learn-more-button"
          >
            {t($ => $.common.actions.learnMore)}
          </Button>,
        ] : undefined}
        icon={(
          <MaterialSymbol
            fill
            icon="calendar_clock"
          />
        )}
        onClose={banner.isDismissible ? handleDismiss : undefined}
        status={banner.status}
        title={getTitle()}
      />
      {showLearnMoreDialog && !!accessToken && (
        <ProgramExpirationLearnMoreDialog
          accessToken={accessToken}
          daysUntilProgramEnd={daysUntilProgramEnd}
          onClose={() => setShowLearnMoreDialog(false)}
          status={banner.status}
        />
      )}
    </>
  );
};
